// Toolbar.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Editor } from "@tiptap/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faItalic,
  faUnderline,
  faStrikethrough,
  faCode,
  faListUl,
  faListOl,
  faQuoteRight,
  faImage,
  faVideo,
  faUndo,
  faRedo,
  faPalette,
  faHighlighter,
  faTable
} from "@fortawesome/free-solid-svg-icons";
import "@/css/rich-text-editor.css";

/*
===============================================================================
=== Toolbar and ToolbarButton components for rich text editor
===============================================================================
*/

type ToolbarCtx = {
  active: number;
  setActive: (i: number) => void;
  register: (el: HTMLButtonElement | null) => number;
  items: React.MutableRefObject<(HTMLButtonElement | null)[]>;
};

const Ctx = createContext<ToolbarCtx | null>(null);

export function Toolbar({
  label,
  children,
  onEscapeTo, // optional: HTMLElement to return focus to on Escape
}: {
  label: string;
  children: React.ReactNode;
  onEscapeTo?: HTMLElement | null;
}) {
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  const [active, setActive] = useState(0);

  const register = (el: HTMLButtonElement | null) => {
    const idx = items.current.indexOf(el);
    if (idx === -1) items.current.push(el);
    return items.current.indexOf(el);
  };

  const ctx = useMemo(() => ({ active, setActive, register, items }), [active]);

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    const len = items.current.length;
    if (!len) return;

    const move = (next: number) => {
      setActive((next + len) % len);
      items.current[(next + len) % len]?.focus();
    };

    console.log("Toolbar keydown:", e.key);

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        move(active + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        move(active - 1);
        break;
      case "Home":
        e.preventDefault();
        move(0);
        break;
      case "End":
        e.preventDefault();
        move(len - 1);
        break;
      case "Escape":
        if (onEscapeTo) {
          e.preventDefault();
          onEscapeTo.focus();
        }
        break;
    }
  };

  return (
    <div
      role="toolbar"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="border-b border-gray-300 p-2 flex flex-wrap gap-1 bg-gray-50"
    >
      <Ctx.Provider value={ctx}>{children}</Ctx.Provider>
    </div>
  );
}

export type ToolbarButtonProps = {
  "aria-label": string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  icon: any;
  className?: string;
};

export function ToolbarButton({
  "aria-label": ariaLabel,
  onClick,
  active,
  disabled,
  title,
  icon,
  className,
  ...rest
}: ToolbarButtonProps) {
  const ctx = useContext(Ctx);
  const ref = useRef<HTMLButtonElement | null>(null);
  if (!ctx) throw new Error("ToolbarButton must be inside <Toolbar>");

  // register once
  useEffect(() => {
    ctx.register(ref.current);
  }, [ctx]);

  const index = ctx.items.current.indexOf(ref.current);
  const isActive = index === ctx.active;

  // ensure focus follows active item only when toolbar has focus
  useEffect(() => {
    if (isActive && ref.current) {
      // Only focus if the toolbar container or one of its children already has focus
      const toolbarContainer = ref.current.closest('[role="toolbar"]');
      const toolbarHasFocus = toolbarContainer?.contains(document.activeElement);
      
      if (toolbarHasFocus && document.activeElement !== ref.current) {
        ref.current.focus();
      }
    }
  }, [isActive]);

  return (
    <button
      ref={ref}
      type="button"
      // onMouseDown={(e) => e.preventDefault()} // Prevent focus change (maybe?)
      tabIndex={isActive ? 0 : -1}
      aria-pressed={active}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={`p-2 rounded hover:bg-gray-200 ${
        active ? "bg-blue-200" : ""
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
      title={title}
      {...rest}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
}

/*
===============================================================================
=== Actual toolbar used in the rich text editor
===============================================================================
*/

export type RichTextToolbarProps = {
  editorRef: React.RefObject<HTMLElement | null>;
  editor: Editor;
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  // which toolbar blocks to show
  enabledToolbarBlocks: Array<
    "formatting" | "lists" | "tables" | "colors" | "media" | "history"
  >;
};

export function RichTextToolbar({
  editorRef,
  editor,
  isUploading,
  fileInputRef,
  enabledToolbarBlocks = [
    "formatting",
    "lists",
    "tables",
    "colors",
    "media",
    "history",
  ],
}: RichTextToolbarProps) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);

  const addYouTubeVideo = useCallback(() => {
    const url = prompt("Enter YouTube URL:");
    if (url && editor) {
      editor.commands.setYoutubeVideo({
        src: url,
      });
    }
  }, [editor]);

  const insertTable = useCallback(() => {
    if (editor) {
      editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
      setShowTableMenu(false);
    }
  }, [editor]);

  const colors = [
    "#000000",
    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#FFFF00",
    "#FF00FF",
    "#00FFFF",
    "#FFA500",
    "#800080",
    "#808080",
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".relative")) {
        setShowColorPicker(false);
        setShowHighlightPicker(false);
        setShowTableMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const makeQuoteFromSelection = () => {
    // Get text selected on the page (outside the editor)
    const pageSelection = window.getSelection();
    const selectedText = pageSelection?.toString().trim();

    if (selectedText) {
      // If there's text selected on the page, insert it as a blockquote
      editor
        .chain()
        .focus()
        .insertContent(`<blockquote><p>${selectedText}</p></blockquote>`)
        .run();

      // Clear the page selection after using it
      pageSelection?.removeAllRanges();
    } else {
      // If no page selection, check for text selected within the editor
      const editorSelection = editor.state.selection;
      const editorSelectedText = editor.state.doc.textBetween(
        editorSelection.from,
        editorSelection.to
      );

      if (editorSelectedText.trim()) {
        // If there's selected text in editor, create a blockquote with that text
        editor.chain().focus().setBlockquote().run();
      } else {
        // If no selection anywhere, just toggle blockquote
        editor.chain().focus().toggleBlockquote().run();
      }
    }
  };

  const toolbarBlocks = {
    formatting: (
      <div role="group" aria-label="Formatting">
        <ToolbarButton
          key="formatting-bold"
          aria-label="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
          icon={faBold}
        />

        <ToolbarButton
          key="formatting-italic"
          aria-label="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
          icon={faItalic}
        />

        <ToolbarButton
          key="formatting-underline"
          aria-label="Underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
          title="Underline"
          icon={faUnderline}
        />

        <ToolbarButton
          key="formatting-strikethrough"
          aria-label="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
          title="Strikethrough"
          icon={faStrikethrough}
        />

        <ToolbarButton
          key="formatting-code"
          aria-label="Code"
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive("code")}
          title="Code"
          icon={faCode}
        />

        <ToolbarButton
          key="formatting-blockquote"
          aria-label="Blockquote"
          onClick={makeQuoteFromSelection}
          active={editor.isActive("blockquote")}
          title="Quote"
          icon={faQuoteRight}
        />
      </div>
    ),
    lists: (
      <div role="group" aria-label="Lists">
        <ToolbarButton
          aria-label="Bullet List"
          key="lists-bullet"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bullet List"
          icon={faListUl}
        />

        <ToolbarButton
          aria-label="Numbered List"
          key="lists-numbered"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Numbered List"
          icon={faListOl}
        />
      </div>
    ),
    tables: (
      <div role="group" aria-label="Tables">
        <div className="relative flex items-center" key="tables-table-menu">
          <ToolbarButton
            aria-label="Table"
            onClick={() => setShowTableMenu(!showTableMenu)}
            active={editor.isActive("table")}
            title="Table"
            icon={faTable}
          />

          {showTableMenu && (
            <div className="absolute top-10 left-0 bg-white border border-gray-300 rounded-lg p-2 shadow-lg z-10 min-w-[200px]">
              <button
                type="button"
                onClick={insertTable}
                className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
              >
                Insert Table
              </button>
              {editor.isActive("table") && (
                <>
                  <hr className="my-1" />
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().addColumnBefore().run();
                      setShowTableMenu(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                  >
                    Add Column Before
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().addColumnAfter().run();
                      setShowTableMenu(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                  >
                    Add Column After
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().deleteColumn().run();
                      setShowTableMenu(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                  >
                    Delete Column
                  </button>
                  <hr className="my-1" />
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().addRowBefore().run();
                      setShowTableMenu(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                  >
                    Add Row Before
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().addRowAfter().run();
                      setShowTableMenu(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                  >
                    Add Row After
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().deleteRow().run();
                      setShowTableMenu(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm"
                  >
                    Delete Row
                  </button>
                  <hr className="my-1" />
                  <button
                    type="button"
                    onClick={() => {
                      editor.chain().focus().deleteTable().run();
                      setShowTableMenu(false);
                    }}
                    className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm text-red-600"
                  >
                    Delete Table
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    ),

    colors: (
      <div role="group" aria-label="Colors">
        <div className="relative flex items-center" key="colors-color-menu">
          <ToolbarButton
            aria-label="colors-color-menu"
            active={showColorPicker}
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="p-2 rounded hover:bg-gray-200"
            title="Text Color"
            icon={faPalette}
          />

          {showColorPicker && (
            <div
              style={{ minWidth: "150px" }}
              className="absolute top-10 left-0 bg-white border border-gray-300 rounded-lg p-2 shadow-lg z-10"
            >
              <div className="grid grid-cols-5 gap-1">
                {colors.map((color) => (
                  <button
                    type="button"
                    key={color}
                    onClick={() => {
                      editor.chain().focus().setColor(color).run();
                      setShowColorPicker(false);
                    }}
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setShowColorPicker(false);
                }}
                className="mt-2 text-xs px-2 py-1 bg-gray-200 rounded"
              >
                Clear
              </button>
            </div>
          )}

          <ToolbarButton
            aria-label="colors-highlight-menu"
            active={showHighlightPicker}
            onClick={() => setShowHighlightPicker(!showHighlightPicker)}
            className="p-2 rounded hover:bg-gray-200 relative"
            title="Highlight"
            icon={faHighlighter}
          />

          {showHighlightPicker && (
            <div
              style={{ minWidth: "150px" }}
              className="absolute top-10 left-0 bg-white border border-gray-300 rounded-lg p-2 shadow-lg z-10"
            >
              <div className="grid grid-cols-5 gap-1">
                {colors.map((color) => (
                  <button
                    type="button"
                    key={color}
                    onClick={() => {
                      editor.chain().focus().setHighlight({ color }).run();
                      setShowHighlightPicker(false);
                    }}
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              <button
                onClick={() => {
                  editor.chain().focus().unsetHighlight().run();
                  setShowHighlightPicker(false);
                }}
                className="mt-2 text-xs px-2 py-1 bg-gray-200 rounded"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>
    ),

    media: (
      <div role="group" aria-label="Media">
        <ToolbarButton
          aria-label="Insert Image"
          key="media-insert-image"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
          title="Insert Image"
          icon={faImage}
        />

        <ToolbarButton
          aria-label="Insert YouTube Video"
          key="media-insert-youtube"
          onClick={addYouTubeVideo}
          className="p-2 rounded hover:bg-gray-200"
          title="Insert YouTube Video"
          icon={faVideo}
        />
      </div>
    ),

    history: (
      <div role="group" aria-label="History">
        <ToolbarButton
          aria-label="Undo"
          key="history-undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
          title="Undo"
          icon={faUndo}
        />

        <ToolbarButton
          aria-label="Redo"
          key="history-redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
          title="Redo"
          icon={faRedo}
        />
      </div>
    ),
  };

  return (
    <Toolbar label="RTE Toolbar" onEscapeTo={editorRef.current}>
      {enabledToolbarBlocks.map((block, index) => (
        <React.Fragment key={`toolbar-block-${block}`}>
          {toolbarBlocks[block]}
          {index < enabledToolbarBlocks.length - 1 && (
            <div
              key={`toolbar-block-${block}-separator`}
              className="w-px h-6 my-3 bg-gray-400 mx-1"
            />
          )}
        </React.Fragment>
      ))}
    </Toolbar>
  );
}
